import type {
  DefaultSharedCoreModuleContext,
  LangiumCoreServices,
  LangiumSharedCoreServices,
  Module,
  PartialLangiumCoreServices,
} from 'langium';
import {
  EmptyFileSystem,
  createDefaultCoreModule,
  createDefaultSharedCoreModule,
  inject,
} from 'langium';

import {
  MermaidGeneratedSharedModule,
  WardleyGrammarGeneratedModule as WardleyGeneratedModule,
} from '../generated/module.js';
import { WardleyValueConverter } from './valueConverter.js';

/**
 * Declaration of `Wardley` services.
 */
interface WardleyAddedServices {
  parser: {
    ValueConverter: WardleyValueConverter;
  };
}

/**
 * Union of Langium default services and `Wardley` services.
 */
export type WardleyServices = LangiumCoreServices & WardleyAddedServices;

/**
 * Dependency injection module that overrides Langium default services and
 * contributes the declared `Wardley` services.
 */
export const WardleyModule: Module<
  WardleyServices,
  PartialLangiumCoreServices & WardleyAddedServices
> = {
  parser: {
    ValueConverter: () => new WardleyValueConverter(),
  },
};

/**
 * Create the full set of services required by Langium.
 *
 * First inject the shared services by merging two modules:
 *  - Langium default shared services
 *  - Services generated by langium-cli
 *
 * Then inject the language-specific services by merging three modules:
 *  - Langium default language-specific services
 *  - Services generated by langium-cli
 *  - Services specified in this file
 * @param context - Optional module context with the LSP connection
 * @returns An object wrapping the shared services and the language-specific services
 */
export function createWardleyServices(context: DefaultSharedCoreModuleContext = EmptyFileSystem): {
  shared: LangiumSharedCoreServices;
  Wardley: WardleyServices;
} {
  const shared: LangiumSharedCoreServices = inject(
    createDefaultSharedCoreModule(context),
    MermaidGeneratedSharedModule
  );
  const Wardley: WardleyServices = inject(
    createDefaultCoreModule({ shared }),
    WardleyGeneratedModule,
    WardleyModule
  );
  shared.ServiceRegistry.register(Wardley);
  return { shared, Wardley };
}
